import { takeEvery, put, call, select } from 'redux-saga/effects';
import { google as googleApi, geolocation as geolocationApi } from 'flux/api';
import {
  user as userActions,
  application as applicationActions,
  ui as uiActions,
} from 'flux/actions';

import { ui as uiSelectors, property as propertySelectors } from 'flux/selectors';

import { v4 as uuidv4 } from 'uuid';

import { webStorage, formatGeolocation } from 'utils';
import { getI18nMessages } from 'utils/i18n';

import errorHandler from 'errorHandler';

export function* initUser() {
  const userIdMappingKey = 'userId';
  const userGeolocationKey = 'userGeolocation';
  const { id: propertyId } = yield select(propertySelectors.selectProperty);

  let userIdMapping = {};
  try {
    userIdMapping = JSON.parse(webStorage.getItem(userIdMappingKey)) || {};
  } catch (e) {
    // If the value stored at the webStorageKey is not a JSON format, delete it
    webStorage.removeItem(userIdMappingKey);
  }

  const userId = userIdMapping[propertyId] || uuidv4();
  userIdMapping[propertyId] = userId;

  yield put(userActions.setId(userId));

  webStorage.setItem(userIdMappingKey, JSON.stringify(userIdMapping));

  try {
    const geolocation = JSON.parse(webStorage.getItem(userGeolocationKey));
    if (geolocation) {
      yield put(userActions.setGeolocation(formatGeolocation(geolocation, 'user')));
    }
  } catch (e) {
    // Nothing happens
  }
}

export function* getCurrentPosition() {
  yield put(applicationActions.setLoadingGeolocation(true));
  try {
    const {
      coords: { latitude: lat, longitude: lng },
    } = yield call(geolocationApi.getCurrentPosition, {
      maximumAge: 30000,
      timeout: 5000,
    });
    yield put(userActions.geocodeAndSetGeolocation({ lat, lng }));
  } catch (e) {
    errorHandler(e);
    const language = yield select(uiSelectors.selectLanguage);
    const { geolocation_error: geolocationError, understand } = getI18nMessages(language);
    yield put(uiActions.toggleSnackbar(true, geolocationError, understand));
    yield put(applicationActions.setLoadingGeolocation(false));
  }
}

export function setGeolocation({ payload: { geolocation } }) {
  webStorage.setItem('userGeolocation', JSON.stringify(geolocation));
}

export function* geocodeAndSetGeolocation({ payload: { location } }) {
  try {
    const label = yield call(googleApi.geocode, location);
    yield put(userActions.setGeolocation(formatGeolocation({ ...location, label }, 'user')));
  } catch (e) {
    errorHandler(e);
  } finally {
    yield put(applicationActions.setLoadingGeolocation(false));
  }
}

export default function* () {
  yield takeEvery(applicationActions.types.INIT, initUser);
  yield takeEvery(userActions.types.GET_CURRENT_POSITION, getCurrentPosition);
  yield takeEvery(userActions.types.SET_GEOLOCATION, setGeolocation);
  yield takeEvery(userActions.types.GEOCODE_AND_SET_GEOLOCATION, geocodeAndSetGeolocation);
}
